import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { ENEMY_DROP_TABLE, LOOT_RARITY, ROOM_CLEAR_REWARD_TABLE } from '../config/loot.js';
import { createBillboardSprite } from '../engine/billboardSprite.js';
import { WEAPONS } from '../data/weapons.data.js';
import { showPickupNotification } from './ui/pickupNotification.js';

const PICKUP_RADIUS = 0.6;
const TOOLTIP_DISTANCE = 1.5;
const WEAPON_ICON_PATHS = Object.freeze({
  shinkansen_pistol: '/assets/2D/IconWeapon/Pistol/Pistol_Horizontal_01.png',
  reiketsu_shotgun: '/assets/2D/IconWeapon/Shotgun/Shotgun_Horizontal_01.png',
  jinsoku_smg: '/assets/2D/IconWeapon/Uzi/Uzi_Horizontal_01.png',
  stormcaller_smr2: '/assets/2D/IconWeapon/Rifle/Rifle_Horizontal_01.png',
  tenrai_mortar: '/assets/2D/IconWeapon/Mortar/Mortar_Horizontal_01.png',
  jigoku_launcher: '/assets/2D/IconWeapon/AssaultRifle/AssaultRifle_Horizontal_01.png',
});

function weightedPick(rng, weights, fallback) {
  const entries = Object.entries(weights || {});
  if (entries.length === 0) return fallback;
  let total = 0;
  for (const [, w] of entries) total += Math.max(0, Number(w) || 0);
  if (total <= 0) return fallback ?? entries[0][0];
  let roll = (rng?.() ?? Math.random()) * total;
  for (const [id, weight] of entries) {
    roll -= Math.max(0, Number(weight) || 0);
    if (roll <= 0) return id;
  }
  return entries[entries.length - 1][0];
}

function hasWeaponEquipped(player, weaponId) {
  const left = player?.hands?.left?.weaponId;
  const right = player?.hands?.right?.weaponId;
  return left === weaponId || right === weaponId;
}

function selectWeaponIdByRarity(rng, rarity) {
  const pool = WEAPONS.filter((w) => w.rarity === rarity);
  if (pool.length === 0) return null;
  const idx = Math.floor((rng?.() ?? Math.random()) * pool.length);
  return pool[idx]?.id || null;
}

function createTooltipLabel(text) {
  const el = document.createElement('div');
  el.className = 'world-pickup-tooltip';
  el.textContent = text;
  return new CSS2DObject(el);
}

function createFloorDecal(color) {
  const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.22, depthWrite: false });
  const mesh = new THREE.Mesh(new THREE.CircleGeometry(0.62, 24), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.01;
  return mesh;
}

function createLegendaryRing(color) {
  const ring = new THREE.Group();
  for (let i = 0; i < 8; i++) {
    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 8, 8),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 }),
    );
    ring.add(dot);
  }
  return ring;
}

function roomClearBand(roomNumber) {
  return ROOM_CLEAR_REWARD_TABLE.find((entry) => roomNumber >= entry.min && roomNumber <= entry.max)
    || ROOM_CLEAR_REWARD_TABLE[ROOM_CLEAR_REWARD_TABLE.length - 1];
}

/**
 * Spawn a pickup in world space and register its update/collision metadata.
 * @param {import('./game.js').Game} game
 * @param {{
 *   x: number,
 *   z: number,
 *   kind: 'weapon'|'ammo'|'hp',
 *   weaponId?: string,
 *   rarity?: string,
 *   label?: string,
 *   amount?: number,
 * }} spec
 */
export function spawnWorldPickup(game, spec) {
  const rarityKey = String(spec.rarity || 'common').toLowerCase();
  const rarity = LOOT_RARITY[rarityKey] || LOOT_RARITY.common;
  const root = new THREE.Group();
  root.position.set(spec.x, 0, spec.z);
  const weaponIconPath = spec.weaponId
    ? (WEAPON_ICON_PATHS[spec.weaponId] || `/assets/icons/weapons/${spec.weaponId}.png`)
    : '/assets/icons/weapons/question.png';
  const sprite = createBillboardSprite(weaponIconPath, { size: 1.1 });
  sprite.position.set(0, 0.85, 0);
  root.add(sprite);

  let light = null;
  let decal = null;
  let ring = null;
  if (rarity.glowColor && rarity.lightIntensity > 0) {
    light = new THREE.PointLight(rarity.glowColor, rarity.lightIntensity, 3.2, 2);
    light.position.set(0, 0.2, 0);
    root.add(light);
    decal = createFloorDecal(rarity.glowColor);
    root.add(decal);
    if (rarity.particleRing) {
      ring = createLegendaryRing(rarity.glowColor);
      ring.position.set(0, 0.22, 0);
      root.add(ring);
    }
  }

  const tooltipText = `${spec.label || 'Pickup'} [${rarity.label}]`;
  const tooltip = createTooltipLabel(tooltipText);
  tooltip.position.set(0, 1.6, 0);
  tooltip.visible = false;
  root.add(tooltip);

  game.scene.add(root);
  game.lootPickups.push({
    kind: spec.kind,
    x: spec.x,
    z: spec.z,
    radius: PICKUP_RADIUS,
    t: game.rng() * Math.PI * 2,
    root,
    sprite,
    light,
    ring,
    rarityKey,
    weaponId: spec.weaponId || null,
    amount: spec.amount || 0,
    tooltip,
    label: spec.label || 'Pickup',
  });
}

/**
 * Roll and spawn loot when an enemy dies.
 * @param {import('./game.js').Game} game
 * @param {string} enemyType
 * @param {{ x: number, z: number }} position
 */
export function generateEnemyDrop(game, enemyType, position) {
  const table = ENEMY_DROP_TABLE[enemyType];
  if (!table) return;
  if ((game.rng?.() ?? Math.random()) > table.chance) return;
  const rarity = weightedPick(game.rng, table.rarityWeights, 'common');
  const weaponId = selectWeaponIdByRarity(game.rng, rarity);
  if (!weaponId) return;
  if (hasWeaponEquipped(game.player, weaponId)) {
    spawnWorldPickup(game, {
      x: position.x,
      z: position.z,
      kind: 'ammo',
      rarity: 'common',
      label: 'Small Ammo Pack',
      amount: 8,
    });
    return;
  }
  const weapon = WEAPONS.find((w) => w.id === weaponId);
  game._spawnGroundWeaponPickupById?.(
    weaponId,
    { x: position.x, y: 0, z: position.z },
    rarity,
  );
  if (!weapon) return;
}

/**
 * Spawn room clear bonus pickup near the room center.
 * @param {import('./game.js').Game} game
 * @param {number} roomNumber
 */
export function roomClearBonus(game, roomNumber) {
  const band = roomClearBand(roomNumber);
  const reward = weightedPick(game.rng, band.rewardWeights, 'weapon');
  const bounds = game.room?.getAtmosphereBounds?.();
  const cx = bounds ? (bounds.minX + bounds.maxX) * 0.5 : 0;
  const cz = bounds ? (bounds.minZ + bounds.maxZ) * 0.5 : 0;
  const x = cx + (game.rng() * 2 - 1) * 0.9;
  const z = cz + (game.rng() * 2 - 1) * 0.9;

  if (reward === 'hp') {
    spawnWorldPickup(game, { x, z, kind: 'hp', rarity: 'uncommon', label: 'Small HP Restore', amount: 1 });
  } else if (reward === 'ammo') {
    spawnWorldPickup(game, { x, z, kind: 'ammo', rarity: 'common', label: 'Large Ammo Pack', amount: 16 });
  } else {
    const rarity = weightedPick(game.rng, band.weaponRarityWeights, 'common');
    const weaponId = selectWeaponIdByRarity(game.rng, rarity);
    if (!weaponId) return;
    if (hasWeaponEquipped(game.player, weaponId)) {
      spawnWorldPickup(game, { x, z, kind: 'ammo', rarity: 'common', label: 'Large Ammo Pack', amount: 16 });
    } else {
      const weapon = WEAPONS.find((w) => w.id === weaponId);
      spawnWorldPickup(game, { x, z, kind: 'weapon', weaponId, rarity, label: weapon?.name || weaponId });
    }
  }
  game.audio?.play?.('roomClearBonus');
  game.hud?.showRoomClearBonus?.();
}

/**
 * Per-frame update for loot bobbing, glow, tooltip visibility, and pickup collision.
 * @param {import('./game.js').Game} game
 * @param {number} dt
 */
export function updateLootPickups(game, dt) {
  const player = game.player;
  if (!player?.alive || game.state !== 'playing') {
    for (const pickup of game.lootPickups) {
      pickup.tooltip.visible = false;
    }
    return;
  }
  let nearestPickup = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let i = game.lootPickups.length - 1; i >= 0; i--) {
    const pickup = game.lootPickups[i];
    pickup.t += dt;
    pickup.sprite.position.y = 0.85 + Math.sin(pickup.t * 2.4) * 0.09;
    if (pickup.light) {
      const rarity = LOOT_RARITY[pickup.rarityKey] || LOOT_RARITY.common;
      const pulse = rarity.pulseSpeed > 0 ? (0.85 + Math.sin(pickup.t * rarity.pulseSpeed) * 0.15) : 1;
      const flicker = rarity.lightFlicker > 0 ? (1 + (Math.random() * 2 - 1) * rarity.lightFlicker) : 1;
      pickup.light.intensity = rarity.lightIntensity * pulse * flicker;
    }
    if (pickup.ring) {
      const children = pickup.ring.children;
      for (let idx = 0; idx < children.length; idx++) {
        const angle = pickup.t * 2.5 + (idx / children.length) * Math.PI * 2;
        const node = children[idx];
        node.position.set(Math.cos(angle) * 0.46, 0.04 + Math.sin(pickup.t * 4 + idx) * 0.04, Math.sin(angle) * 0.46);
      }
    }
    const distance = Math.hypot(player.x - pickup.x, player.z - pickup.z);
    pickup.tooltip.visible = false;
    if (distance <= TOOLTIP_DISTANCE && distance < nearestDistance) {
      nearestDistance = distance;
      nearestPickup = pickup;
    }
    if (distance > player.radius + pickup.radius) continue;

    if (pickup.kind === 'weapon' && pickup.weaponId) {
      game._spawnGroundWeaponPickupById?.(
        pickup.weaponId,
        { x: pickup.x, y: 0, z: pickup.z },
        pickup.rarityKey,
      );
      game.audio?.play?.('itemPickup', { rate: 1.05 + Math.random() * 0.05 });
      game._notifyNewWeaponPickup?.(pickup.weaponId);
      showPickupNotification({
        icon: `public/assets/icons/weapons/${pickup.weaponId}.png`,
        label: 'Found',
        itemName: pickup.label || pickup.weaponId,
        rarity: pickup.rarityKey || 'default',
      });
    } else if (pickup.kind === 'ammo') {
      const gained = player.addReserveAmmo(pickup.amount || 8);
      if (gained > 0) {
        game.audio?.play?.('itemPickup', { rate: 1.07 + Math.random() * 0.05 });
        game.toast(`+${gained} AMMO`);
        showPickupNotification({
          icon: 'public/assets/icons/ui/ammo.png',
          label: 'Obtained',
          itemName: `${gained}x Ammo`,
          rarity: 'default',
        });
      }
    } else if (pickup.kind === 'hp') {
      const before = player.hp;
      player.heal(1);
      if (player.hp > before) {
        game.audio?.play?.('itemPickup', { rate: 1.1 });
        game.toast('+1 HP');
        const isFull = Number.isFinite(player.maxHp) && player.hp >= player.maxHp;
        showPickupNotification({
          icon: 'public/assets/icons/ui/hp.png',
          label: 'Obtained',
          itemName: isFull ? 'Medicine (Full)' : 'Medicine (Partial)',
          rarity: 'default',
        });
      }
    }

    pickup.tooltip.visible = false;
    pickup.tooltip.removeFromParent?.();
    game.scene.remove(pickup.root);
    game.lootPickups.splice(i, 1);
  }
  if (nearestPickup && game.lootPickups.includes(nearestPickup)) {
    nearestPickup.tooltip.visible = true;
  }
}

/**
 * Dispose all world loot instances from scene.
 * @param {import('./game.js').Game} game
 */
export function clearLootPickups(game) {
  for (const pickup of game.lootPickups) game.scene.remove(pickup.root);
  game.lootPickups.length = 0;
}
